import { useEffect, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Bell, CalendarRange, CheckSquare, ChevronRight, LayoutDashboard, Landmark, LogOut, Menu, Users, Wrench, type LucideIcon } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { NAVIGATION } from './navigation';
import { InstallPrompt } from './InstallPrompt';

export function useBadges() {
  const { can } = useAuth();
  const bank = useQuery({
    queryKey: ['badge', 'bank'],
    queryFn: () => api.get<Record<string, { count: number }>>('/bank/transactions/summary'),
    enabled: can('bank.read'),
    refetchInterval: 60_000,
  });
  const dunning = useQuery({
    queryKey: ['badge', 'dunning'],
    queryFn: () => api.get<{ total: number }>('/dunning/notices', { status: 'ENTWURF,FREIGEGEBEN', pageSize: 1 }),
    enabled: can('dunning.read'),
    refetchInterval: 60_000,
  });
  const b = bank.data;
  return {
    bank: b ? (b.NEU?.count ?? 0) + (b.VORGESCHLAGEN?.count ?? 0) + (b.NICHT_ZUGEORDNET?.count ?? 0) : 0,
    dunning: dunning.data?.total ?? 0,
  } as Record<string, number>;
}

interface TabDef {
  to: string;
  label: string;
  icon: LucideIcon;
  permission?: string;
  badgeKey?: string;
}

/** Kandidaten für die Tab-Leiste – die ersten vier erlaubten werden angezeigt, dazu „Mehr“ */
const TAB_CANDIDATES: TabDef[] = [
  { to: '/', label: 'Start', icon: LayoutDashboard },
  { to: '/mietuebersicht', label: 'Mieten', icon: CalendarRange, permission: 'finance.read' },
  { to: '/bank', label: 'Bank', icon: Landmark, permission: 'bank.read', badgeKey: 'bank' },
  { to: '/aufgaben', label: 'Aufgaben', icon: CheckSquare, permission: 'tasks.read' },
  { to: '/mieter', label: 'Mieter', icon: Users, permission: 'tenants.read' },
  { to: '/schaeden', label: 'Schäden', icon: Wrench, permission: 'tickets.read' },
];

const isActivePath = (pathname: string, to: string) => (to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`));

/** Farbige Symbolkacheln je Navigationsgruppe (wie in iOS-Einstellungen) */
const GROUP_TONES = ['bg-brand-600', 'bg-emerald-600', 'bg-sky-600', 'bg-amber-500', 'bg-violet-600', 'bg-slate-600'];

/**
 * Mobile Navigation wie in einer App: feste Tab-Leiste unten und eine „Mehr“-Seite mit allen Bereichen.
 * Nur unterhalb von lg sichtbar; auf dem Desktop bleibt die Seitenleiste.
 */
export function MobileTabBar({ moreOpen, onMore }: { moreOpen: boolean; onMore: (open: boolean) => void }) {
  const { can } = useAuth();
  const { pathname } = useLocation();
  const badges = useBadges();
  const tabs = TAB_CANDIDATES.filter((t) => !t.permission || can(t.permission)).slice(0, 4);
  const onTab = tabs.some((t) => isActivePath(pathname, t.to));
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200/80 bg-white/90 pb-safe backdrop-blur-xl lg:hidden print:hidden" aria-label="Hauptnavigation">
      <ul className="mx-auto grid max-w-xl" style={{ gridTemplateColumns: `repeat(${tabs.length + 1}, minmax(0, 1fr))` }}>
        {tabs.map((t) => {
          const count = t.badgeKey ? badges[t.badgeKey] : 0;
          const active = !moreOpen && isActivePath(pathname, t.to);
          return (
            <li key={t.to}>
              <NavLink to={t.to} end={t.to === '/'} onClick={() => onMore(false)} className="flex flex-col items-center pb-1.5 pt-2" aria-current={active ? 'page' : undefined}>
                <TabIcon icon={t.icon} active={active} count={count} />
                <span className={clsx('mt-0.5 max-w-full truncate px-1 text-[10.5px] font-medium', active ? 'text-brand-700' : 'text-slate-500')}>{t.label}</span>
              </NavLink>
            </li>
          );
        })}
        <li>
          <button type="button" onClick={() => onMore(!moreOpen)} className="flex w-full flex-col items-center pb-1.5 pt-2" aria-expanded={moreOpen}>
            <TabIcon icon={Menu} active={moreOpen || !onTab} />
            <span className={clsx('mt-0.5 text-[10.5px] font-medium', moreOpen || !onTab ? 'text-brand-700' : 'text-slate-500')}>Mehr</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}

function TabIcon({ icon: Icon, active, count = 0 }: { icon: LucideIcon; active: boolean; count?: number }) {
  return (
    <span className={clsx('relative flex h-7 w-12 items-center justify-center rounded-full transition-colors', active ? 'bg-brand-100 text-brand-800' : 'text-slate-500')}>
      <Icon className="h-[21px] w-[21px]" strokeWidth={active ? 2.3 : 1.9} />
      {count > 0 && (
        <span className="absolute -right-0.5 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white tabular ring-2 ring-white">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </span>
  );
}

export function MoreSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, can, logout } = useAuth();
  const badges = useBadges();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const unread = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.get<{ count: number }>('/notifications/unread-count'),
    enabled: open,
    retry: false,
  });

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open || !user) return null;
  const unreadCount = unread.data?.count ?? 0;

  return (
    <div className="anim-sheet fixed inset-0 z-[35] overflow-y-auto overscroll-contain bg-slate-100 pb-tabbar pt-safe lg:hidden" role="dialog" aria-modal="true" aria-label="Menü">
      <div className="mx-auto max-w-xl px-4 pt-4">
        <h2 className="px-1 text-[28px] font-bold tracking-tight text-slate-900">Menü</h2>

        {/* Profilkarte */}
        <Link to="/profil" onClick={onClose} className="mt-3 flex items-center gap-3 rounded-2xl bg-white p-3.5 shadow-sm active:bg-slate-50">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-100 text-base font-semibold text-brand-800">
            {user.firstName[0]}
            {user.lastName[0]}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-semibold text-slate-900">
              {user.firstName} {user.lastName}
            </span>
            <span className="block truncate text-sm text-slate-500">
              {user.role.name} · Profil & Sicherheit
            </span>
          </span>
          <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />
        </Link>

        <Section>
          <Row to="/benachrichtigungen" icon={Bell} tone="bg-red-500" label="Benachrichtigungen" count={unreadCount} active={pathname === '/benachrichtigungen'} onClick={onClose} />
        </Section>

        {NAVIGATION.map((group, gi) => {
          const items = group.items.filter((i) => (!i.permission || can(i.permission)) && !(i.hideWithPermission && can(i.hideWithPermission)));
          if (!items.length) return null;
          return (
            <div key={group.label}>
              <p className="mb-1.5 mt-6 px-4 text-[12px] font-medium uppercase tracking-wide text-slate-500">{group.label}</p>
              <Section flush>
                {items.map((item) => (
                  <Row
                    key={item.to}
                    to={item.to}
                    icon={item.icon}
                    tone={GROUP_TONES[gi % GROUP_TONES.length]!}
                    label={item.label}
                    count={item.badgeKey ? badges[item.badgeKey] : 0}
                    active={isActivePath(pathname, item.to)}
                    onClick={onClose}
                  />
                ))}
              </Section>
            </div>
          );
        })}

        <Section>
          <InstallPrompt variant="menu" onAction={onClose} />
          <button
            type="button"
            className="flex w-full items-center gap-3 px-3.5 py-3 text-left text-[15px] font-medium text-red-600 active:bg-red-50"
            onClick={async () => {
              onClose();
              await logout();
              navigate('/login');
            }}
          >
            <LogOut className="h-5 w-5" /> Abmelden
          </button>
        </Section>
        <p className="mt-6 pb-4 text-center text-xs text-slate-400">Mietverwaltung Portal</p>
      </div>
    </div>
  );
}

function Section({ children, flush }: { children: ReactNode; flush?: boolean }) {
  return <div className={clsx('divide-y divide-slate-100 overflow-hidden rounded-2xl bg-white shadow-sm', !flush && 'mt-5')}>{children}</div>;
}

function Row({ to, icon: Icon, tone, label, count = 0, active, onClick }: { to: string; icon: LucideIcon; tone: string; label: string; count?: number; active?: boolean; onClick: () => void }) {
  return (
    <Link to={to} onClick={onClick} className={clsx('flex items-center gap-3 px-3.5 py-2.5 active:bg-slate-100', active && 'bg-brand-50/60')}>
      <span className={clsx('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white', tone)}>
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <span className={clsx('min-w-0 flex-1 truncate text-[15px]', active ? 'font-semibold text-brand-800' : 'text-slate-800')}>{label}</span>
      {count > 0 && <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-semibold text-white tabular">{count > 99 ? '99+' : count}</span>}
      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
    </Link>
  );
}
